import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import { ArrowLeft, CheckCircle2, FileText, User, Package } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const PURCHASE_SOURCES = [
  'Ebay', 'Worten', 'Miravia', 'Aliexpress', 'Carrefour',
  'Pixmania', 'Temu', 'Amazon', 'ARREGLATELO.ES',
  'ZONADEJUEGO.ES', 'MUNDODESONRISAS.ES'
];

const SPANISH_COMMUNITIES = [
  'Andalucía', 'Aragón', 'Asturias', 'Baleares', 'Canarias',
  'Cantabria', 'Castilla-La Mancha', 'Castilla y León', 'Cataluña',
  'Extremadura', 'Galicia', 'La Rioja', 'Madrid', 'Murcia',
  'Navarra', 'País Vasco', 'Valencia', 'Ceuta', 'Melilla'
];

const InvoiceForm = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [responseData, setResponseData] = useState(null);
  
  const [formData, setFormData] = useState({
    full_name: '',
    nif_cif_dni: '',
    street_name: '',
    street_number: '',
    piso: '',
    puerta: '',
    postal_code: '',
    comunidad: '',
    municipio: '',
    country: 'España',
    phone: '',
    email: '',
    product_name: '',
    serial_number: '',
    purchase_date: '',
    purchase_source: '',
    order_id: ''
  });

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const response = await axios.post(
        `${API_URL}/api/invoice`,
        formData,
        { withCredentials: true }
      );
      setResponseData(response.data);
      setSuccess(true);
      toast.success('Solicitud de factura enviada');
    } catch (error) {
      const detail = error.response?.data?.detail;
      const message = typeof detail === 'string' ? detail : 
        Array.isArray(detail) ? detail.map(e => e.msg || e).join(' ') : 
        'Error al enviar la solicitud';
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  const nextStep = () => setStep(s => Math.min(s + 1, 2));
  const prevStep = () => setStep(s => Math.max(s - 1, 1));

  if (success) {
    return (
      <div className="min-h-screen bg-[#F5F5F7] flex items-center justify-center p-4" data-testid="invoice-success">
        <div className="bg-white rounded-xl border border-[#E5E7EB] p-8 max-w-lg w-full text-center">
          <div className="w-20 h-20 bg-[#10B981]/10 rounded-full flex items-center justify-center mx-auto mb-6">
            <CheckCircle2 className="w-10 h-10 text-[#10B981]" />
          </div>
          <h1 className="text-2xl font-bold mb-4">Solicitud Enviada</h1>
          <p className="text-[#52525B] mb-6">
            Tu solicitud de factura ha sido recibida. Recibirás la factura por email una vez procesada.
          </p>
          
          {responseData && (
            <div className="bg-[#F5F5F7] rounded-lg p-4 mb-6 text-left">
              <p className="font-mono text-sm">
                <span className="text-[#52525B]">Referencia:</span>{' '}
                <span className="font-bold">{responseData.invoice_request_id?.slice(0, 12).toUpperCase()}</span>
              </p>
            </div>
          )}
          
          <div className="flex gap-4">
            <Button
              variant="outline"
              className="flex-1"
              onClick={() => navigate('/invoice')}
              data-testid="view-invoices-btn"
            >
              {t('dashboard.invoice.check')}
            </Button>
            <Button
              className="flex-1 bg-[#0047FF] hover:bg-[#003BCC]"
              onClick={() => navigate('/dashboard')}
              data-testid="back-to-dashboard-btn"
            >
              {t('nav.dashboard')}
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F5F5F7]" data-testid="invoice-form-page">
      {/* Header */}
      <header className="bg-white border-b border-[#E5E7EB]">
        <div className="max-w-3xl mx-auto px-4 py-4">
          <Link 
            to="/dashboard" 
            className="inline-flex items-center gap-2 text-[#52525B] hover:text-[#0A0A0A] transition-colors"
            data-testid="back-link"
          >
            <ArrowLeft className="w-4 h-4" />
            {t('common.back')}
          </Link>
        </div>
      </header>

      {/* Form */}
      <main className="max-w-3xl mx-auto px-4 py-8">
        <h1 className="text-2xl font-bold mb-2">{t('invoice.title')}</h1>
        
        {/* Progress Steps */}
        <div className="flex items-center gap-2 mb-8">
          {[1, 2].map((s) => (
            <React.Fragment key={s}>
              <div
                className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-semibold transition-colors ${
                  step >= s ? 'bg-[#0047FF] text-white' : 'bg-[#E5E7EB] text-[#52525B]'
                }`}
              >
                {s}
              </div>
              {s < 2 && (
                <div className={`flex-1 h-1 ${step > s ? 'bg-[#0047FF]' : 'bg-[#E5E7EB]'}`}></div>
              )}
            </React.Fragment>
          ))}
        </div>

        <form onSubmit={handleSubmit}>
          {/* Step 1: Personal Info */}
          {step === 1 && (
            <div className="bg-white rounded-xl border border-[#E5E7EB] p-6">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 bg-[#0047FF]/10 rounded-lg flex items-center justify-center">
                  <User className="w-5 h-5 text-[#0047FF]" />
                </div>
                <h2 className="text-lg font-semibold">{t('warranty.personal')}</h2>
              </div>

              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>{t('warranty.fullName')} *</Label>
                    <Input
                      value={formData.full_name}
                      onChange={(e) => handleChange('full_name', e.target.value)}
                      className="mt-1"
                      required
                      data-testid="full-name-input"
                    />
                  </div>
                  <div>
                    <Label>{t('invoice.nif')} *</Label>
                    <Input
                      value={formData.nif_cif_dni}
                      onChange={(e) => handleChange('nif_cif_dni', e.target.value)}
                      className="mt-1 font-mono"
                      required
                      placeholder="12345678A"
                      data-testid="nif-input"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-3 gap-4">
                  <div className="col-span-2">
                    <Label>{t('warranty.street')} *</Label>
                    <Input
                      value={formData.street_name}
                      onChange={(e) => handleChange('street_name', e.target.value)}
                      className="mt-1"
                      required
                      data-testid="street-input"
                    />
                  </div>
                  <div>
                    <Label>{t('warranty.number')} *</Label>
                    <Input
                      value={formData.street_number}
                      onChange={(e) => handleChange('street_number', e.target.value)}
                      className="mt-1"
                      required
                      data-testid="number-input"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>{t('warranty.piso')}</Label>
                    <Input
                      value={formData.piso}
                      onChange={(e) => handleChange('piso', e.target.value)}
                      className="mt-1"
                      data-testid="piso-input"
                    />
                  </div>
                  <div>
                    <Label>{t('warranty.puerta')}</Label>
                    <Input
                      value={formData.puerta}
                      onChange={(e) => handleChange('puerta', e.target.value)}
                      className="mt-1"
                      data-testid="puerta-input"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>{t('warranty.postalCode')} *</Label>
                    <Input
                      value={formData.postal_code}
                      onChange={(e) => handleChange('postal_code', e.target.value)}
                      className="mt-1"
                      required
                      data-testid="postal-code-input"
                    />
                  </div>
                  <div>
                    <Label>{t('warranty.municipio')} *</Label>
                    <Input
                      value={formData.municipio}
                      onChange={(e) => handleChange('municipio', e.target.value)}
                      className="mt-1"
                      required
                      data-testid="municipio-input"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>{t('warranty.comunidad')} *</Label>
                    <Select value={formData.comunidad} onValueChange={(v) => handleChange('comunidad', v)}>
                      <SelectTrigger className="mt-1" data-testid="comunidad-select">
                        <SelectValue placeholder="Seleccionar..." />
                      </SelectTrigger>
                      <SelectContent>
                        {SPANISH_COMMUNITIES.map((c) => (
                          <SelectItem key={c} value={c}>{c}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>{t('warranty.country')} *</Label>
                    <Input
                      value={formData.country}
                      onChange={(e) => handleChange('country', e.target.value)}
                      className="mt-1"
                      required
                      data-testid="country-input"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>{t('warranty.phone')} *</Label>
                    <Input
                      type="tel"
                      value={formData.phone}
                      onChange={(e) => handleChange('phone', e.target.value)}
                      className="mt-1"
                      required
                      data-testid="phone-input"
                    />
                  </div>
                  <div>
                    <Label>{t('warranty.email')} *</Label>
                    <Input
                      type="email"
                      value={formData.email}
                      onChange={(e) => handleChange('email', e.target.value)}
                      className="mt-1"
                      required
                      data-testid="email-input"
                    />
                  </div>
                </div>
              </div>

              <div className="flex justify-end mt-6">
                <Button
                  type="button"
                  onClick={nextStep}
                  className="bg-[#0047FF] hover:bg-[#003BCC]"
                  data-testid="next-step-btn"
                >
                  {t('common.next')}
                </Button>
              </div>
            </div>
          )}

          {/* Step 2: Product Info */}
          {step === 2 && (
            <div className="bg-white rounded-xl border border-[#E5E7EB] p-6">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 bg-[#10B981]/10 rounded-lg flex items-center justify-center">
                  <Package className="w-5 h-5 text-[#10B981]" />
                </div>
                <h2 className="text-lg font-semibold">{t('warranty.product')}</h2>
              </div>

              <div className="space-y-4">
                <div>
                  <Label>{t('warranty.productName')} *</Label>
                  <Input
                    value={formData.product_name}
                    onChange={(e) => handleChange('product_name', e.target.value)}
                    className="mt-1"
                    required
                    data-testid="product-name-input"
                  />
                </div>

                <div>
                  <Label>{t('warranty.serialNumber')}</Label>
                  <Input
                    value={formData.serial_number}
                    onChange={(e) => handleChange('serial_number', e.target.value)}
                    className="mt-1 font-mono"
                    data-testid="serial-number-input"
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>{t('warranty.purchaseDate')} *</Label>
                    <Input
                      type="date"
                      value={formData.purchase_date}
                      onChange={(e) => handleChange('purchase_date', e.target.value)}
                      className="mt-1"
                      required
                      data-testid="purchase-date-input"
                    />
                  </div>
                  <div>
                    <Label>{t('warranty.purchaseSource')} *</Label>
                    <Select value={formData.purchase_source} onValueChange={(v) => handleChange('purchase_source', v)}>
                      <SelectTrigger className="mt-1" data-testid="purchase-source-select">
                        <SelectValue placeholder="Seleccionar..." />
                      </SelectTrigger>
                      <SelectContent>
                        {PURCHASE_SOURCES.map((s) => (
                          <SelectItem key={s} value={s}>{s}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div>
                  <Label>{t('warranty.orderId')} *</Label>
                  <Input
                    value={formData.order_id}
                    onChange={(e) => handleChange('order_id', e.target.value)}
                    className="mt-1 font-mono"
                    required
                    data-testid="order-id-input"
                  />
                </div>
              </div>

              <div className="flex justify-between mt-6">
                <Button
                  type="button"
                  variant="outline"
                  onClick={prevStep}
                  data-testid="prev-step-btn"
                >
                  {t('common.previous')}
                </Button>
                <Button
                  type="submit"
                  className="bg-[#0047FF] hover:bg-[#003BCC]"
                  disabled={loading}
                  data-testid="submit-btn"
                >
                  {loading ? t('common.loading') : t('invoice.submit')}
                </Button>
              </div>
            </div>
          )}
        </form>
      </main>
    </div>
  );
};

export default InvoiceForm;
